import type { MappingEditorState } from '@/helpers/schemaMappingEditor.helpers';

export const SCHEMA_MAPPING_DRAFT_VERSION = 1;
export const SCHEMA_MAPPING_DRAFT_PREFIX = 'review-ui:schema-mapping-draft:v1:';
const NO_SOURCE = '__no-source__';

/**
 * What the browser keeps for one mapping. `unsaved` is work the project has
 * not seen — the API refuses a structurally incomplete mapping, so until it
 * can be saved it lives here and is drawn dotted. `ignoredColumns` is kept
 * apart so it survives a successful save; it is browser-only either way.
 */
export interface SchemaMappingDraftEnvelope {
  version: number;
  projectId: string;
  schemaId: string;
  schemaVersionId: string;
  source: string | null;
  updatedAt: string;
  unsaved: MappingEditorState | null;
  ignoredColumns: string[];
}

const availableStorage = (storage?: Storage): Storage | null => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const encode = (value: string) => encodeURIComponent(value);

export const schemaMappingDraftKey = (
  projectId: string,
  schemaId: string,
  source: string | null,
): string =>
  `${SCHEMA_MAPPING_DRAFT_PREFIX}${encode(projectId)}:${encode(schemaId)}:${source ? encode(source) : NO_SOURCE}`;

export function writeSchemaMappingDraft(
  key: string,
  envelope: SchemaMappingDraftEnvelope,
  storage?: Storage,
): boolean {
  const target = availableStorage(storage);
  if (!target) return false;
  try {
    target.setItem(key, JSON.stringify(envelope));
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads a draft, discarding anything not written by this version of the code
 * or written for another project or schema under a colliding key.
 */
export function readSchemaMappingDraft(
  key: string,
  expected: { projectId: string; schemaId: string },
  storage?: Storage,
): SchemaMappingDraftEnvelope | null {
  const target = availableStorage(storage);
  if (!target) return null;
  let raw: string | null = null;
  try {
    raw = target.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !isRecord(parsed) ||
      parsed.version !== SCHEMA_MAPPING_DRAFT_VERSION ||
      parsed.projectId !== expected.projectId ||
      parsed.schemaId !== expected.schemaId ||
      typeof parsed.schemaVersionId !== 'string' ||
      typeof parsed.updatedAt !== 'string' ||
      !Array.isArray(parsed.ignoredColumns)
    ) {
      target.removeItem(key);
      return null;
    }
    return parsed as unknown as SchemaMappingDraftEnvelope;
  } catch {
    try {
      target.removeItem(key);
    } catch {
      // Nothing to do: a broken entry that cannot be removed is still ignored.
    }
    return null;
  }
}

/** Drops the unsaved work but keeps the author's ignored columns. */
export function clearUnsavedDraft(
  key: string,
  expected: { projectId: string; schemaId: string },
  storage?: Storage,
): boolean {
  const current = readSchemaMappingDraft(key, expected, storage);
  if (!current) return false;
  if (current.unsaved === null) return true;
  return writeSchemaMappingDraft(
    key,
    { ...current, unsaved: null, updatedAt: new Date().toISOString() },
    storage,
  );
}
/** Keys of this project's drafts that hold work the project has not seen. */
export function listSchemaMappingDraftKeys(
  projectId: string,
  storage?: Storage,
): string[] {
  const target = availableStorage(storage);
  if (!target) return [];
  const prefix = `${SCHEMA_MAPPING_DRAFT_PREFIX}${encode(projectId)}:`;
  const keys: string[] = [];
  try {
    for (let index = 0; index < target.length; index += 1) {
      const key = target.key(index);
      if (!key || !key.startsWith(prefix)) continue;
      const raw = target.getItem(key);
      if (!raw) continue;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (isRecord(parsed) && parsed.unsaved !== null && parsed.unsaved !== undefined) {
          keys.push(key);
        }
      } catch {
        // Skip entries that do not parse; the next read removes them.
      }
    }
  } catch {
    return [];
  }
  return keys;
}

/**
 * The newest draft holding unsaved work for one schema, whatever file it was
 * against.
 *
 * A mapping that has never been saved has no source to key its draft by until
 * the reader picks a file, and the key then moves with that choice. Reopening
 * such a mapping therefore cannot look the draft up by key: it has to look for
 * the schema. Whichever draft was written last is the one the reader left.
 */
export function findUnsavedDraftForSchema(
  projectId: string,
  schemaId: string,
  options: {
    /**
     * Sources this schema already has a saved mapping for. A draft against one
     * of them belongs to that mapping, not to a new one the author has just
     * asked to start.
     */
    excludeSources?: ReadonlyArray<string>;
    storage?: Storage;
  } = {},
): { key: string; envelope: SchemaMappingDraftEnvelope } | null {
  const storage = options.storage;
  const excluded = new Set(options.excludeSources ?? []);
  const target = availableStorage(storage);
  if (!target) return null;
  const prefix = `${SCHEMA_MAPPING_DRAFT_PREFIX}${encode(projectId)}:${encode(schemaId)}:`;
  let best: { key: string; envelope: SchemaMappingDraftEnvelope } | null = null;
  try {
    for (let index = 0; index < target.length; index += 1) {
      const key = target.key(index);
      if (!key || !key.startsWith(prefix)) continue;
      const envelope = readSchemaMappingDraft(key, { projectId, schemaId }, storage);
      if (!envelope?.unsaved) continue;
      if (envelope.source !== null && excluded.has(envelope.source)) continue;
      if (!best || envelope.updatedAt > best.envelope.updatedAt) {
        best = { key, envelope };
      }
    }
  } catch {
    return null;
  }
  return best;
}

export function clearAllSchemaMappingDrafts(storage?: Storage): void {
  const target = availableStorage(storage);
  if (!target) return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < target.length; index += 1) {
      const key = target.key(index);
      if (key?.startsWith(SCHEMA_MAPPING_DRAFT_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => target.removeItem(key));
  } catch {
    // Storage that cannot be enumerated has nothing this code can clear.
  }
}
